import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api.js";
import { useLiveFight } from "../hooks/useLiveFight.js";
import { useActionQueue } from "../hooks/useActionQueue.js";
import { useWakeLock } from "../hooks/useWakeLock.js";
import { TapButton } from "../components/TapButton.js";
import { FinishModal } from "../components/FinishModal.js";
import type {
  ActionType,
  FighterSide,
  FighterStats,
  LiveFightPayload,
  Outcome,
} from "@mma/shared";

function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function SpotterPage() {
  const { data } = useLiveFight(1000);
  const queue = useActionQueue();
  const wakeLock = useWakeLock(true);
  const [controlActive, setControlActive] = useState<{ a: boolean; b: boolean }>({
    a: false,
    b: false,
  });
  const [showFinish, setShowFinish] = useState(false);

  if (!data || !data.fight_id) {
    return (
      <div className="min-h-full flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="text-2xl font-bold mb-3">Ingen live kamp</h1>
          <p className="text-neutral-400 mb-6">
            Aktiver en kamp i admin før spotter kan registrere.
          </p>
          <Link to="/" className="text-blue-400 underline">
            ← Tilbage
          </Link>
        </div>
      </div>
    );
  }

  const fightId = data.fight_id;

  const log = (
    fighter: FighterSide,
    action_type: ActionType,
    outcome: Outcome,
  ) => {
    queue.enqueue(fightId, {
      action_type,
      outcome,
      fighter,
      round: data.current_round,
      time_in_round_sec: data.round_elapsed_sec,
    });
  };

  const toggleControl = (fighter: FighterSide) => {
    const isActive = controlActive[fighter];
    log(
      fighter,
      isActive ? "control_time_stop" : "control_time_start",
      null,
    );
    setControlActive((s) => ({ ...s, [fighter]: !isActive }));
  };

  const undo = async (fighter?: FighterSide) => {
    const qs = fighter ? `?fightId=${fightId}&fighter=${fighter}` : `?fightId=${fightId}`;
    try {
      await api.post(`/api/actions/undo${qs}`);
    } catch {
      // ignore — viste i UI
    }
  };

  const timerAction = async (action: "start" | "pause" | "next_round" | "reset") => {
    try {
      await api.post(`/api/fights/${fightId}/timer`, { action });
    } catch {
      // ignore
    }
  };

  return (
    <div className="h-dvh overflow-hidden flex flex-col bg-neutral-950">
      <RoundBar
        data={data}
        onTimer={timerAction}
        pending={queue.pending}
        onUndo={() => undo()}
        onFinish={() => setShowFinish(true)}
        wakeLockHeld={wakeLock.held}
        wakeLockSupported={wakeLock.supported}
      />
      {queue.lastError && (
        <div className="bg-red-700 text-white text-sm px-4 py-2 flex items-center justify-between">
          <span>
            <strong>Fejl ved gem:</strong> {queue.lastError}
            {queue.authFailed && " — log ind igen i en anden fane og kom tilbage"}
          </span>
          <span className="font-mono">{queue.pending} i kø</span>
        </div>
      )}
      <div className="flex-1 grid grid-cols-2 gap-1 p-1">
        <FighterPanel
          side="a"
          data={data}
          onLog={log}
          onUndo={() => undo("a")}
          controlActive={controlActive.a}
          onToggleControl={() => toggleControl("a")}
        />
        <FighterPanel
          side="b"
          data={data}
          onLog={log}
          onUndo={() => undo("b")}
          controlActive={controlActive.b}
          onToggleControl={() => toggleControl("b")}
        />
      </div>
      {showFinish && data.fighter_a && data.fighter_b && (
        <FinishModal
          fightId={fightId}
          totalRounds={data.total_rounds}
          currentRound={data.current_round}
          fighterAName={data.fighter_a.name}
          fighterACorner={data.fighter_a.corner}
          fighterBName={data.fighter_b.name}
          fighterBCorner={data.fighter_b.corner}
          onClose={() => setShowFinish(false)}
          onFinished={() => setShowFinish(false)}
        />
      )}
    </div>
  );
}

function RoundBar({
  data,
  onTimer,
  onUndo,
  onFinish,
  pending,
  wakeLockHeld,
  wakeLockSupported,
}: {
  data: LiveFightPayload;
  onTimer: (a: "start" | "pause" | "next_round" | "reset") => void;
  onUndo: () => void;
  onFinish: () => void;
  pending: number;
  wakeLockHeld: boolean;
  wakeLockSupported: boolean;
}) {
  const remaining = Math.max(0, data.round_duration_sec - data.round_elapsed_sec);
  return (
    <header className="flex items-center gap-2 p-2 bg-neutral-900 border-b border-neutral-800">
      <Link to="/" className="px-3 py-2 text-sm text-neutral-400">
        ←
      </Link>
      <div className="flex flex-col leading-tight">
        <span className="text-xs uppercase text-neutral-400">
          Runde {data.current_round} / {data.total_rounds}
        </span>
        <span className="text-2xl font-mono tabular-nums font-bold">
          {fmtTime(remaining)}
        </span>
      </div>
      <div className="flex-1" />
      <WakeLockBadge held={wakeLockHeld} supported={wakeLockSupported} />
      {data.round_running ? (
        <TapButton variant="warning" onClick={() => onTimer("pause")} className="px-4">
          ⏸
        </TapButton>
      ) : (
        <TapButton variant="primary" onClick={() => onTimer("start")} className="px-4">
          ▶
        </TapButton>
      )}
      <TapButton variant="neutral" onClick={() => onTimer("next_round")} className="px-3 text-sm">
        Næste runde
      </TapButton>
      <TapButton variant="neutral" onClick={onUndo} className="px-3 text-sm">
        ↶ Undo
      </TapButton>
      <TapButton variant="warning" onClick={onFinish} className="px-3 text-sm">
        Afslut kamp
      </TapButton>
      {pending > 0 && (
        <span className="text-xs text-amber-400 ml-2">{pending} i kø</span>
      )}
    </header>
  );
}

function WakeLockBadge({ held, supported }: { held: boolean; supported: boolean }) {
  if (!supported) {
    return (
      <span
        className="text-[10px] uppercase text-amber-400/80"
        title="Browser understøtter ikke Wake Lock — iPad kan gå i sleep. Sluk auto-lock i iPad Settings."
      >
        ⚠ no-lock
      </span>
    );
  }
  return (
    <span
      className={`text-[10px] uppercase ${held ? "text-emerald-400" : "text-neutral-500"}`}
      title={held ? "Skærm holdes vågen" : "Wake lock ikke aktiv"}
    >
      {held ? "● awake" : "○ awake"}
    </span>
  );
}

function FighterPanel({
  side,
  data,
  onLog,
  onUndo,
  controlActive,
  onToggleControl,
}: {
  side: FighterSide;
  data: LiveFightPayload;
  onLog: (s: FighterSide, t: ActionType, o: Outcome) => void;
  onUndo: () => void;
  controlActive: boolean;
  onToggleControl: () => void;
}) {
  const f = side === "a" ? data.fighter_a : data.fighter_b;
  if (!f) return null;
  const cornerBg = f.corner === "red" ? "bg-red-cornerDark" : "bg-blue-cornerDark";
  const cornerAccent = f.corner === "red" ? "border-red-corner" : "border-blue-corner";

  const strikes: { label: string; type: ActionType }[] = [
    { label: "Jab", type: "jab" },
    { label: "Cross", type: "cross" },
    { label: "Hook", type: "hook" },
    { label: "Uppercut", type: "uppercut" },
    { label: "Head kick", type: "head_kick" },
    { label: "Body kick", type: "body_kick" },
    { label: "Low kick", type: "low_kick" },
  ];

  const stat = (key: keyof FighterStats): number => Number(f[key] ?? 0);

  const strikeStats = (t: ActionType): { landed: number; thrown: number } => {
    switch (t) {
      case "jab":
        return { landed: stat("jab_landed"), thrown: stat("jab_thrown") };
      case "cross":
        return { landed: stat("cross_landed"), thrown: stat("cross_thrown") };
      case "hook":
        return { landed: stat("hook_landed"), thrown: stat("hook_thrown") };
      case "uppercut":
        return { landed: stat("uppercut_landed"), thrown: stat("uppercut_thrown") };
      case "low_kick":
        return { landed: stat("low_kick_landed"), thrown: stat("low_kick_thrown") };
      case "body_kick":
        return { landed: stat("body_kick_landed"), thrown: stat("body_kick_thrown") };
      case "head_kick":
        return { landed: stat("head_kick_landed"), thrown: stat("head_kick_thrown") };
      default:
        return { landed: 0, thrown: 0 };
    }
  };

  return (
    <div
      className={`flex flex-col rounded-lg overflow-hidden border-2 ${cornerAccent} ${cornerBg}`}
    >
      <div className="p-3 bg-black/30">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-xl font-bold truncate">{f.name}</h2>
          <span className="text-xs uppercase opacity-75">{f.corner}</span>
        </div>
        <div className="text-sm opacity-80 truncate">
          {[f.country, f.club, f.record].filter(Boolean).join(" · ")}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
          <Stat label="Strikes" value={`${stat("total_strikes_landed")} / ${stat("total_strikes_thrown")}`} />
          <Stat label="Acc %" value={`${stat("total_strikes_accuracy_pct")}`} />
          <Stat label="TD" value={`${stat("takedowns_landed")} / ${stat("takedowns_attempted")}`} />
          <Stat label="KD" value={`${stat("knockdowns")}`} />
          <Stat label="Sub" value={`${stat("submissions_successful")} / ${stat("submissions_attempted")}`} />
          <Stat label="Ctrl" value={fmtTime(stat("control_time_sec"))} />
        </div>
      </div>

      <div className="p-2 space-y-2 flex-1 overflow-y-auto">
        {strikes.map((s) => {
          const c = strikeStats(s.type);
          return (
            <div key={s.type} className="grid grid-cols-[auto_1fr_1fr] gap-2 items-center">
              <div className="w-20 text-sm font-semibold">
                {s.label}
                <div className="text-xs opacity-70 tabular-nums">
                  {c.landed} / {c.thrown}
                </div>
              </div>
              <TapButton variant="landed" onClick={() => onLog(side, s.type, "landed")}>
                Landed
              </TapButton>
              <TapButton variant="missed" onClick={() => onLog(side, s.type, "missed")}>
                Missed
              </TapButton>
            </div>
          );
        })}

        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
          <TapButton variant="landed" onClick={() => onLog(side, "takedown", "successful")}>
            TD landet
          </TapButton>
          <TapButton variant="missed" onClick={() => onLog(side, "takedown", "attempted")}>
            TD forsøgt
          </TapButton>
          <TapButton variant="landed" onClick={() => onLog(side, "submission", "successful")}>
            Sub landet
          </TapButton>
          <TapButton variant="missed" onClick={() => onLog(side, "submission", "attempted")}>
            Sub forsøgt
          </TapButton>
          <TapButton variant="warning" onClick={() => onLog(side, "knockdown", null)}>
            Knockdown
          </TapButton>
          <TapButton
            variant={controlActive ? "primary" : "neutral"}
            onClick={onToggleControl}
          >
            {controlActive ? "Control STOP" : "Control START"}
          </TapButton>
        </div>

        <TapButton variant="neutral" onClick={onUndo} className="w-full mt-2 text-sm">
          ↶ Undo {f.name.split(" ")[0]}
        </TapButton>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-black/30 rounded px-2 py-1">
      <div className="text-[10px] uppercase opacity-60">{label}</div>
      <div className="font-mono tabular-nums text-sm">{value}</div>
    </div>
  );
}
